/**
 * TriangularPrism Component (SVG)
 *
 * Renders a 3D triangular prism using accurate SVG isometric projection.
 * Displays as a "tent" shape (triangular base on the front/back, extending along length).
 */

import React from 'react';
import { Box } from '@mui/material';

interface TriangularPrismProps {
  base?: number;
  height?: number;
  length?: number;
  unit?: string;
  color?: string;
  strokeColor?: string;
}

export const TriangularPrism: React.FC<TriangularPrismProps> = ({
  base = 4,
  height = 3,
  length = 6,
  unit = 'cm',
  color = '#e0e7ff',
  strokeColor = '#9c27b0'
}) => {
  // Center the prism in the viewBox
  const centerX = 50;
  const centerY = 55; // Slightly lower to account for the height
  
  // Normalized sizes for visual consistency
  const maxDim = Math.max(length, base, height);
  const scale = 30 / maxDim; // Base scale factor
  
  const L = length * scale;
  const W = base * scale;
  const H = height * scale;
  
  // Isometric projection math
  const cos30 = 0.8660254;
  const sin30 = 0.5;
  
  const project = (x: number, y: number, z: number) => ({
    x: centerX + (x - y) * cos30,
    y: centerY + (x + y) * sin30 - z
  });
  
  // Calculate vertices: x=length, y=base, z=height
  // Tent shape: base in XY plane, ridge at Z=H
  const p000 = project(0, 0, 0); // Back corner
  const pL00 = project(L, 0, 0); // Right corner
  const p0W0 = project(0, W, 0); // Left corner
  const pLW0 = project(L, W, 0); // Front corner
  const p0mH = project(0, W/2, H); // Back ridge
  const pLmH = project(L, W/2, H); // Front ridge
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        {/* Hidden edges (dashed) */}
        <line x1={p000.x} y1={p000.y} x2={pL00.x} y2={pL00.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={p000.x} y1={p000.y} x2={p0W0.x} y2={p0W0.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={p000.x} y1={p000.y} x2={p0mH.x} y2={p0mH.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        
        {/* Visible Faces with shading */}
        {/* Right Roof */}
        <polygon
          points={`${p000.x},${p000.y} ${pL00.x},${pL00.y} ${pLmH.x},${pLmH.y} ${p0mH.x},${p0mH.y}`}
          fill={color}
          opacity={0.4}
        />
        {/* Left Roof */}
        <polygon
          points={`${p0W0.x},${p0W0.y} ${pLW0.x},${pLW0.y} ${pLmH.x},${pLmH.y} ${p0mH.x},${p0mH.y}`}
          fill={color}
          opacity={0.7}
        />
        {/* Front Triangle */}
        <polygon
          points={`${pLW0.x},${pLW0.y} ${pL00.x},${pL00.y} ${pLmH.x},${pLmH.y}`}
          fill={color}
          opacity={0.9}
        />

        {/* Visible Edges */}
        {/* Outer silhouette and inner lines */}
        <polygon
          points={`${p0W0.x},${p0W0.y} ${pLW0.x},${pLW0.y} ${pL00.x},${pL00.y} ${pLmH.x},${pLmH.y} ${p0mH.x},${p0mH.y}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        <line x1={pLW0.x} y1={pLW0.y} x2={pLmH.x} y2={pLmH.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <line x1={pLmH.x} y1={pLmH.y} x2={pL00.x} y2={pL00.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <line x1={p0mH.x} y1={p0mH.y} x2={pLmH.x} y2={pLmH.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        
        {/* Corner points */}
        <circle cx={p0W0.x} cy={p0W0.y} r={1.2} fill={strokeColor} />
        <circle cx={pLW0.x} cy={pLW0.y} r={1.2} fill={strokeColor} />
        <circle cx={pL00.x} cy={pL00.y} r={1.2} fill={strokeColor} />
        <circle cx={pLmH.x} cy={pLmH.y} r={1.2} fill={strokeColor} />
        <circle cx={p0mH.x} cy={p0mH.y} r={1.2} fill={strokeColor} />
        
        {/* Dimension labels */}
        {/* h (height) - vertical line from front ridge to base */}
        <line 
          x1={pLmH.x} y1={pLmH.y} 
          x2={pLmH.x} y2={pLmH.y + H} 
          stroke={strokeColor} strokeWidth={1} strokeDasharray="1,2" opacity={0.6} 
        />
        <text
          x={pLmH.x - 4}
          y={pLmH.y + H / 2 + 2}
          fontSize={3.5}
          fill="#374151"
          textAnchor="end"
          fontWeight={600}
        >
          h = {height} {unit}
        </text>
        
        {/* base - aligned below the front triangle base */}
        <text
          x={(pLW0.x + pL00.x) / 2}
          y={(pLW0.y + pL00.y) / 2 + 6}
          fontSize={3.5}
          fill="#374151"
          textAnchor="middle"
          fontWeight={600}
        >
          base = {base} {unit}
        </text>
        
        {/* length - aligned below the left-bottom edge */}
        <text
          x={(p0W0.x + pLW0.x) / 2 - 4}
          y={(p0W0.y + pLW0.y) / 2 + 6}
          fontSize={3.5}
          fill="#374151"
          textAnchor="end"
          fontWeight={600}
        >
          l = {length} {unit}
        </text>
      </svg>
    </Box>
  );
};

export default TriangularPrism;
